// Site Progress UI kit: theme tokens, thin line icons (24px grid, 1.5px stroke,
// square caps, no fills — same drawing rules as www.brxs.ca/icons), buttons,
// chips, status pill, segmented control and a custom date picker.
import { useEffect, useId, useRef, useState } from 'react';
import { T, F, I, Btn } from '../../lib/bricks';

export const THEMES = {
  light: {
    name: 'light', bg: T.paper, surf: '#FFFFFF', surf2: T.paperSoft, hair: 'rgba(20,17,14,0.10)', hairS: 'rgba(20,17,14,0.24)',
    ink: T.ink, ink2: T.inkSoft, mute: T.inkMute, faint: T.inkFaint, solid: T.ink, solidText: T.paper,
    ok: T.ok, warn: T.warn, bad: T.bad, badTint: T.badTint,
  },
  dark: {
    name: 'dark', bg: '#121110', surf: '#1A1816', surf2: '#24211E', hair: 'rgba(244,241,235,0.09)', hairS: 'rgba(244,241,235,0.24)',
    ink: T.moon, ink2: T.moonSoft, mute: T.moonMute, faint: T.moonFaint, solid: T.moon, solidText: T.ink,
    ok: '#9DB886', warn: '#D9A35C', bad: '#E39A8E', badTint: 'rgba(227,154,142,0.10)',
  },
};
export const theme = (name) => THEMES[name] || THEMES.light;

// The 3D stage is graphite in both page themes.
export const STAGE = {
  bg: '#1A1917', line: 'rgba(244,241,235,0.045)', lineS: 'rgba(244,241,235,0.10)', mark: 'rgba(244,241,235,0.38)',
  panel: 'rgba(20,18,16,0.82)', hair: 'rgba(244,241,235,0.14)', ink: T.moon, mute: T.moonMute, clear: 0x1a1917,
};
export { F, T };

// Names used here -> the shared app icon set (lib/bricks I: 24 grid, 1.5px, square caps, mitred joins, no fills).
const NAME = {
  scan: 'scan', model: 'tour3d', cloud: 'pointCloud', map: 'layers', elevation: 'elevation', photos: 'media', video: 'film',
  report: 'deal', file: 'docs', measure: 'measure', reset: 'resetView', full: 'fullscreen', unfull: 'fullscreenExit', close: 'close',
  download: 'download', open: 'external', compare: 'compare', chevron: 'chevronDown', left: 'chevronLeft', right: 'chevronRight',
  check: 'check', queued: 'queued', alert: 'warn', site: 'siteProgress', link: 'link', play: 'playFrame', gallery: 'gallery',
};

export function Icon({ name, size = 20, color = 'currentColor', style }) {
  return <I name={NAME[name] || name} size={size} color={color} style={{ display: 'block', ...style }} />;
}

export const KIND_ICON = {
  site_scan: 'scan', model_3d: 'model', point_cloud: 'cloud', orthomosaic: 'map', elevation: 'elevation',
  stills: 'photos', video: 'video', report: 'report', other: 'file',
};

const base = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxSizing: 'border-box',
  borderRadius: 2, fontFamily: F.display, fontWeight: 500, letterSpacing: -0.1, cursor: 'pointer',
  textDecoration: 'none', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent',
};

// The shared 48px button (lib/bricks Btn): primary (ink on paper, off-white on night),
// secondary (graphite outline), tertiary (text). compact = 44px for dense rows.
export function Button({ variant = 'primary', tone = 'light', compact, icon, iconRight, href, children, style, ...rest }) {
  const t = theme(tone);
  const tertiary = variant === 'tertiary';
  const s = { minWidth: 44, ...(compact ? { minHeight: 44 } : null), ...(tertiary ? { background: 'transparent', color: t.ink, border: '1px solid transparent', padding: '0 10px' } : null), ...style };
  const linkProps = href ? { as: 'a', href, target: '_blank', rel: 'noreferrer' } : { type: 'button' };
  return (
    <Btn variant={variant === 'primary' ? 'primary' : 'ghost'} dark={tone === 'dark'} style={s} {...linkProps} {...rest}>
      {icon ? <Icon name={icon} size={16} /> : null}{children}{iconRight ? <Icon name={iconRight} size={16} /> : null}
    </Btn>
  );
}

// 44px square icon button for overlays and toolbars.
export function IconButton({ icon, label, onClick, href, stage = true, tone = 'light', pressed, style, ...rest }) {
  const t = theme(tone);
  const s = {
    ...base, width: 44, height: 44, padding: 0,
    background: stage ? STAGE.panel : 'transparent', color: stage ? STAGE.ink : t.ink,
    border: `1px solid ${stage ? STAGE.hair : t.hairS}`, ...style,
  };
  if (href) return <a href={href} target="_blank" rel="noreferrer" aria-label={label} title={label} style={s} {...rest}><Icon name={icon} size={20} /></a>;
  return (
    <button type="button" aria-label={label} title={label} aria-pressed={pressed} onClick={onClick} style={s} {...rest}>
      <Icon name={icon} size={20} />
    </button>
  );
}

// Deliverable chip: icon + label, a real 44px button (or link).
export function Chip({ icon, label, trailing, active, disabled, tone = 'light', href, onClick, note, ...rest }) {
  const t = theme(tone);
  const s = {
    ...base, minHeight: 44, padding: '0 14px 0 12px', fontSize: 14, gap: 10,
    background: active ? t.solid : t.surf, color: active ? t.solidText : disabled ? t.mute : t.ink,
    border: `1px solid ${active ? t.solid : t.hairS}`, cursor: disabled ? 'default' : 'pointer', maxWidth: '100%',
  };
  const kids = (
    <>
      <Icon name={icon} size={18} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {note ? <span style={{ fontSize: 12, color: active ? t.solidText : t.mute, fontWeight: 400 }}>{note}</span> : null}
      {trailing ? <Icon name={trailing} size={16} style={{ opacity: 0.7 }} /> : null}
    </>
  );
  if (href && !disabled) return <a href={href} target="_blank" rel="noreferrer" style={s} {...rest}>{kids}</a>;
  return <button type="button" disabled={disabled} aria-pressed={onClick ? Boolean(active) : undefined} onClick={onClick} style={s} {...rest}>{kids}</button>;
}

const DOT = { delivered: 'ok', processing: 'warn', scheduled: 'mute', failed: 'bad' };

// One-line plain status, top-right of a visit. Sentence case; colour only when it failed.
export function StatusPill({ status, tone = 'light' }) {
  const t = theme(tone);
  const attention = DOT[status.key] === 'bad';
  return (
    <span data-testid="visit-status" style={{
      display: 'inline-flex', alignItems: 'center', minHeight: 28, boxSizing: 'border-box',
      fontFamily: F.display, fontSize: 13, color: attention ? t.bad : t.ink2, whiteSpace: 'nowrap', flex: 'none',
    }}>
      {status.label}
    </span>
  );
}

// Segmented control (44px). options: [{ value, label }]
export function Segmented({ options, value, onChange, label, tone = 'light', stage, style }) {
  const t = theme(tone);
  const on = stage ? { bg: STAGE.ink, fg: '#141210' } : { bg: t.solid, fg: t.solidText };
  return (
    <div role="group" aria-label={label} style={{
      display: 'inline-flex', padding: 0, gap: 0, boxSizing: 'border-box', borderRadius: 2,
      border: `1px solid ${stage ? STAGE.hair : t.hairS}`, background: stage ? STAGE.panel : 'transparent', ...style,
    }}>
      {options.map((o) => {
        const sel = o.value === value;
        return (
          <button key={o.value} type="button" aria-pressed={sel} onClick={() => onChange(o.value)} style={{
            ...base, minHeight: 44, minWidth: 44, padding: '0 16px', fontSize: 13.5, border: 'none', borderRadius: 1,
            background: sel ? on.bg : 'transparent', color: sel ? on.fg : stage ? STAGE.ink : t.ink2,
          }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Custom date picker. options: [{ value, label, disabled }]
export function DatePicker({ label, value, options, onChange, tone = 'light' }) {
  const t = theme(tone);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(0);
  const root = useRef(null);
  const id = useId();
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (root.current && !root.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const show = () => { setFocus(Math.max(0, options.findIndex((o) => o.value === value))); setOpen(true); };
  const pick = (o) => { if (o && !o.disabled) { onChange(o.value); setOpen(false); } };
  const step = (d) => {
    let i = focus;
    for (let k = 0; k < options.length; k++) {
      i = (i + d + options.length) % options.length;
      if (!options[i].disabled) break;
    }
    setFocus(i);
  };
  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!open) show(); else step(e.key === 'ArrowDown' ? 1 : -1); }
    if ((e.key === 'Enter' || e.key === ' ') && open) { e.preventDefault(); pick(options[focus]); }
  };

  return (
    <div ref={root} style={{ position: 'relative', display: 'inline-block', minWidth: 0 }}>
      <button type="button" aria-label={label} aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-list`}
        onClick={() => (open ? setOpen(false) : show())} onKeyDown={onKey}
        style={{ ...base, justifyContent: 'space-between', minHeight: 44, minWidth: 150, padding: '0 12px 0 14px', gap: 12, fontSize: 14,
          background: t.surf, color: t.ink, border: `1px solid ${open ? t.ink : t.hairS}` }}>
        <span>{current ? current.label : 'Pick a date'}</span>
        <Icon name="chevron" size={16} style={{ transform: open ? 'rotate(180deg)' : 'none', color: t.mute }} />
      </button>
      {open ? (
        <ul id={`${id}-list`} role="listbox" aria-label={label} style={{
          position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, minWidth: '100%', maxHeight: 280, overflowY: 'auto',
          margin: 0, padding: 4, listStyle: 'none', boxSizing: 'border-box', background: t.surf, border: `1px solid ${t.hairS}`, borderRadius: 2,
          boxShadow: t.name === 'dark' ? '0 12px 32px rgba(0,0,0,0.45)' : '0 12px 32px rgba(20,17,14,0.12)',
        }}>
          {options.map((o, i) => {
            const sel = o.value === value;
            return (
              <li key={o.value} role="option" aria-selected={sel} aria-disabled={o.disabled || undefined}
                onPointerEnter={() => setFocus(i)} onClick={() => pick(o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minHeight: 44, padding: '0 10px',
                  fontFamily: F.display, fontSize: 14, whiteSpace: 'nowrap', cursor: o.disabled ? 'default' : 'pointer',
                  color: o.disabled ? t.faint : t.ink, background: i === focus && !o.disabled ? t.surf2 : 'transparent' }}>
                <span>{o.label}</span>
                {sel ? <Icon name="check" size={16} /> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
